import { useCallback, useEffect, useState, useMemo } from "react";
import api, { money, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Head, Loader, L } from "./shared";
import { LifecycleTabs } from "@/components/lifecycle/LifecycleTabs";
import { ArchivedPanel } from "@/components/lifecycle/ArchivedPanel";
import { SolutionFactory } from "./SolutionFactory";
import { Plus, Pencil, Copy, X, Loader2, Eye, EyeOff, Star, ExternalLink, Archive, ChevronDown, Sparkles, Upload, CheckCircle2, Circle, ShieldCheck, HelpCircle } from "lucide-react";
import {
  INDUSTRIES, BUSINESS_STAGES, SBA_TAGS, TARGET_CUSTOMERS, DIFFICULTIES,
  STATUSES, VISIBILITIES, AIPOS_COMPATIBILITY, BUSINESS_CAPABILITIES,
  BADGES, SERIES, PAGE_ICONS, PAGE_STATUSES, BUSINESS_CAPABILITY_GROUPS,
} from "./templateOptions";

const emptyTpl = {
  title: "", slug: "", category: "", template_price: 0, custom_price: 0, badge: "",
  regular_price: 0, sale_price: "", series: "", badges: [], pages_detail: [],
  description: "", features: [], pages: [], preview_image: "", demo_url: "", featured: false,
  faqs: [], tech_stack: "", metrics: { performance: 98, accessibility: 100, seo: 100, load_time: 0.8 },
  // V2 factory fields
  industry: "", business_stage: "", sba_tags: [], target_customer: "", difficulty: "", tags: "",
  business_capabilities: [], aipos_compatibility: ["AIPOS Ready"],
  premium: false, best_seller: false, new_release: false, staff_pick: false,
  marketplace_visibility: "Public", status: "Draft",
  seo_title: "", meta_description: "", focus_keywords: "",
  internal_notes: "", ai_notes: "",
  care_classification: "", readiness_exception: false, last_qa_review: "",
};

const CARE_CLASSIFICATIONS = ["Essential", "Growth", "AI Growth", "All plans"];

function ReadinessBar({ r, id }) {
  const [open, setOpen] = useState(false);
  if (!r) return null;
  const pct = r.pct ?? 0;
  const done = r.publishable;
  const color = done
    ? "text-green-300 border-green-500/40 bg-green-500/10"
    : pct >= 75 ? "text-amber-300 border-amber-500/40 bg-amber-500/10" : "text-red-300 border-red-500/40 bg-red-500/10";
  return (
    <div className="mt-2" data-testid={`readiness-${id}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`readiness-toggle-${id}`}
        className={`inline-flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide px-2.5 py-1 border rounded-full ${color}`}>
        <ShieldCheck size={12} /> {done ? "Publish-ready" : "Not ready"} · {r.score}/{r.total}
        {r.exception && !done && <span className="text-[9px] normal-case font-normal opacity-80">exception</span>}
        <ChevronDown size={11} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1" data-testid={`readiness-checks-${id}`}>
          {r.checks.map((c) => (
            <div key={c.key} data-testid={`readiness-check-${id}-${c.key}`} className={`flex items-center gap-1.5 text-[11px] ${c.ok ? "text-gray-300" : "text-gray-500"}`}>
              {c.ok ? <CheckCircle2 size={12} className="text-green-400 shrink-0" /> : <Circle size={12} className="text-gray-600 shrink-0" />}
              <span>{c.label}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const commaToArr = (v) => typeof v === "string" ? v.split(",").map((s) => s.trim()).filter(Boolean) : (v || []);
const slugify = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

export function Templates() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");
  const [status, setStatus] = useState("all");
  const [busyId, setBusyId] = useState(null);
  const [dragId, setDragId] = useState(null);
  const [lifeTab, setLifeTab] = useState("active");
  const [helpOpen, setHelpOpen] = useState(false);

  const load = () => api.get("/admin/templates").then((r) => setItems(r.data)).catch(() => setItems([]));
  useEffect(() => { load(); }, []);

  const categories = useMemo(() => ["All", ...Array.from(new Set((items || []).map((t) => t.category).filter(Boolean))).sort()], [items]);
  const filtered = useMemo(() => (items || []).filter((t) => {
    if (cat !== "All" && t.category !== cat) return false;
    if (status === "active" && !t.is_active) return false;
    if (status === "inactive" && t.is_active) return false;
    if (status === "featured" && !t.featured) return false;
    if (q && !`${t.title} ${t.category} ${t.description}`.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  }), [items, cat, status, q]);

  const save = async (tpl) => {
    try {
      const badges = tpl.badges || [];
      const regular = Number(tpl.regular_price ?? tpl.template_price) || 0;
      const sale = (tpl.sale_price === "" || tpl.sale_price == null) ? null : Number(tpl.sale_price);
      const pagesDetail = (tpl.pages_detail || [])
        .filter((p) => p && p.name && p.name.trim())
        .map((p) => ({ name: p.name.trim(), slug: p.slug || slugify(p.name), icon: p.icon || "file", status: p.status || "Published" }));
      const payload = {
        ...tpl,
        regular_price: regular,
        sale_price: (sale && sale > 0) ? sale : null,
        template_price: (sale && sale > 0) ? sale : regular,   // effective price used at checkout
        custom_price: Number(tpl.custom_price) || 0,
        series: tpl.series || null,
        badges,
        featured: badges.includes("Featured") || !!tpl.featured,
        premium: badges.includes("Premium"),
        best_seller: badges.includes("Best Seller"),
        new_release: badges.includes("New Release"),
        staff_pick: badges.includes("Staff Pick"),
        pages_detail: pagesDetail,
        pages: pagesDetail.map((p) => p.name),
        features: commaToArr(tpl.features),
        tech_stack: commaToArr(tpl.tech_stack), focus_keywords: commaToArr(tpl.focus_keywords),
        tags: commaToArr(tpl.tags),
        sba_tags: tpl.sba_tags || [], business_capabilities: tpl.business_capabilities || [],
        aipos_compatibility: tpl.aipos_compatibility || [],
        metrics: {
          performance: Number(tpl.metrics?.performance ?? 98),
          accessibility: Number(tpl.metrics?.accessibility ?? 100),
          seo: Number(tpl.metrics?.seo ?? 100),
          load_time: Number(tpl.metrics?.load_time ?? 0.8),
        },
        slug: tpl.slug || slugify(tpl.title),
      };
      if (!payload.title) { toast.error("Title is required"); return; }
      if (tpl.id) {
        await api.put(`/templates/${tpl.id}`, payload);
        toast.success("Template saved"); setEditing(null); load();
      } else {
        const { data } = await api.post("/templates", payload);
        toast.success("Template created — now upload your .zip and images below.");
        load();
        openEdit(data); // keep the modal open on the saved template so files can be uploaded right away
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const toggleActive = async (t) => {
    setBusyId(t.id);
    try { const { data } = await api.post(`/templates/${t.id}/toggle-active`); toast.success(data.is_active ? "Template published (live)." : "Template hidden from marketplace."); load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusyId(null); }
  };
  const uploadZipInline = async (t, file) => {
    if (!file) return;
    if (!/\.zip$/i.test(file.name)) { toast.error("Please choose a .zip file."); return; }
    setBusyId(t.id);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("release_type", "minor");
      fd.append("changelog", "Uploaded from Template Factory");
      const { data } = await api.post(`/templates/${t.id}/upload-zip`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Released v${data.version} · ${(data.source_size / 1048576).toFixed(1)}MB${data.live_demo ? " · live demo ready" : ""}`);
      load();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusyId(null); }
  };
  const toggleFeatured = async (t) => {
    setBusyId(t.id);
    try { await api.patch(`/templates/${t.id}/merchandise`, { featured: !t.featured }); toast.success(!t.featured ? "Marked as Featured." : "Removed from Featured."); load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusyId(null); }
  };
  const duplicate = async (t) => {
    setBusyId(t.id);
    try { await api.post(`/templates/${t.id}/duplicate`); toast.success("Duplicated as a hidden draft. Edit & publish when ready."); load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusyId(null); }
  };
  const delPermanent = async (t) => {
    if (!window.confirm(`Archive "${t.title}"? It will move to the Records Center where you can restore or permanently delete it.`)) return;
    setBusyId(t.id);
    try { await api.post(`/lifecycle/templates/${t.id}/archive`, { reason: "" }); toast.success("Template archived. Manage it in Records Center."); load(); }
    catch (e) { toast.error(formatApiError(e)); } finally { setBusyId(null); }
  };

  const openEdit = (t) => setEditing({
    ...emptyTpl, ...t,
    features: (t.features || []).join(", "), pages: (t.pages || []).join(", "),
    tech_stack: (t.tech_stack || []).join(", "), focus_keywords: (t.focus_keywords || []).join(", "),
    tags: (t.tags || []).join(", "),
    regular_price: t.regular_price ?? t.template_price ?? 0,
    sale_price: (t.sale_price === 0 || t.sale_price == null) ? "" : t.sale_price,
    series: t.series || "", badges: t.badges || [],
    pages_detail: (t.pages_detail && t.pages_detail.length)
      ? t.pages_detail
      : (t.pages || []).map((n) => ({ name: n, slug: slugify(n), icon: "file", status: "Published" })),
    sba_tags: t.sba_tags || [], business_capabilities: t.business_capabilities || [],
    aipos_compatibility: t.aipos_compatibility?.length ? t.aipos_compatibility : ["AIPOS Ready"],
    metrics: (t.metrics && Object.keys(t.metrics).length) ? t.metrics : { performance: 98, accessibility: 100, seo: 100, load_time: 0.8 },
  });

  if (!items) return <Loader />;
  const STATUS = [["all", "All"], ["active", "Live"], ["inactive", "Hidden"], ["featured", "Featured"]];
  const STATUS_CLR = { Production: "bg-green-500/15 text-green-300 border-green-500/30", Testing: "bg-blue-500/15 text-blue-300 border-blue-500/30", Draft: "bg-white/10 text-gray-400 border-white/20", Archived: "bg-red-500/10 text-red-300 border-red-500/30" };
  return (
    <div>
      <Head title="Template Factory" desc="Register a new production asset. Drag a .zip onto any card (or use Upload ZIP) to release a version. Optimization, scans & QA happen in later pipeline stages."
        action={<button onClick={() => setEditing({ ...emptyTpl })} data-testid="add-template-btn" className="flex items-center gap-2 bg-gold text-navy px-5 py-2.5 font-semibold hover:bg-gold-light transition-colors"><Plus size={16} /> New Template</button>} />

      {/* HELP — how to fix a broken / placeholder live demo */}
      <div className="mb-5 border border-gold/25 bg-gold/5" data-testid="demo-fix-help">
        <button type="button" onClick={() => setHelpOpen((o) => !o)} data-testid="demo-fix-help-toggle" className="w-full flex items-center justify-between px-4 py-3 text-left">
          <span className="flex items-center gap-2 text-gold text-sm font-medium"><HelpCircle size={15} /> Live demo broken, blank, or showing a “Test” page? How to fix it</span>
          <ChevronDown size={16} className={`text-gold transition-transform ${helpOpen ? "rotate-180" : ""}`} />
        </button>
        {helpOpen && (
          <div className="px-4 pb-4 text-sm text-gray-300 space-y-2.5" data-testid="demo-fix-help-body">
            <p>Each design’s live demo is served from the <strong className="text-white">.zip you upload</strong>. If a demo shows a placeholder (e.g. “Test …”), a blank white page, or the wrong website, it means the wrong .zip was uploaded for that design.</p>
            <p className="text-white font-medium">To fix a design’s live demo:</p>
            <ol className="list-decimal list-inside space-y-1.5">
              <li>Find the design in the list below and click <strong className="text-white">Update ZIP</strong> (or simply drag a .zip file onto its card).</li>
              <li>Choose the correct website .zip. It <strong className="text-white">must contain <code className="bg-navy px-1">index.html</code> at the top level</strong> — not tucked inside a subfolder.</li>
              <li>Uploading releases a new version and <strong className="text-white">automatically re-hosts the live demo</strong>.</li>
              <li>Click <strong className="text-white">Preview</strong> or open the Live Demo to confirm the real site now loads.</li>
            </ol>
            <p className="text-gray-400 text-xs border-t border-white/10 pt-2.5">
              <strong className="text-gray-300">Important:</strong> Preview and the live site (atfwebworks.com) store files separately. If the demo is broken on the <strong>live site</strong>, you must upload the correct .zip here in the <strong>production</strong> admin — a redeploy does not copy files or fix it.
            </p>
          </div>
        )}
      </div>

      <div className="mb-5"><LifecycleTabs module="templates" value={lifeTab} onChange={setLifeTab} refreshKey={(items || []).length} /></div>
      {lifeTab !== "active" ? <ArchivedPanel module="templates" status={lifeTab} singular="Template" onChanged={load} /> : (<>
      <div className="flex flex-wrap items-center gap-3 mb-5">
        <div className="flex gap-1 bg-navy-800 border border-white/10 p-1" data-testid="template-status-filter">
          {STATUS.map(([k, lbl]) => (
            <button key={k} onClick={() => setStatus(k)} data-testid={`template-filter-${k}`}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${status === k ? "bg-gold text-navy" : "text-gray-400 hover:text-white"}`}>
              {lbl}{k === "all" ? ` (${items.length})` : k === "active" ? ` (${items.filter((t) => t.is_active).length})` : k === "inactive" ? ` (${items.filter((t) => !t.is_active).length})` : ` (${items.filter((t) => t.featured).length})`}
            </button>
          ))}
        </div>
        <select value={cat} onChange={(e) => setCat(e.target.value)} data-testid="template-category-filter" className="bg-navy-800 border border-white/10 px-3 py-2 text-sm text-white focus:border-gold focus:outline-none">
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search templates…" data-testid="template-search"
          className="bg-navy-800 border border-white/10 px-3 py-2 text-sm text-white focus:border-gold focus:outline-none flex-1 min-w-[180px]" />
      </div>

      {filtered.length === 0 ? <div className="bg-navy-800 border border-white/5 p-10 text-center text-gray-500 text-sm" data-testid="template-empty">No templates match your filters.</div> : (
        <div className="grid md:grid-cols-2 gap-4">
          {filtered.map((t) => (
            <div key={t.id}
              onDragOver={(e) => { e.preventDefault(); if (busyId !== t.id && dragId !== t.id) setDragId(t.id); }}
              onDragLeave={(e) => { if (e.currentTarget === e.target) setDragId(null); }}
              onDrop={(e) => { e.preventDefault(); setDragId(null); const f = e.dataTransfer?.files?.[0]; uploadZipInline(t, f); }}
              className={`relative bg-navy-800 border p-4 flex gap-4 transition-all ${dragId === t.id ? "border-gold ring-2 ring-gold/40" : t.is_active ? "border-white/5" : "border-white/10 opacity-70"}`} data-testid={`admin-template-${t.id}`}>
              {dragId === t.id && (
                <div className="absolute inset-0 z-10 grid place-items-center bg-navy/85 border-2 border-dashed border-gold pointer-events-none" data-testid={`drop-hint-${t.id}`}>
                  <div className="flex items-center gap-2 text-gold font-medium text-sm"><Upload size={16} /> Drop .zip to release a new version</div>
                </div>
              )}
              {t.preview_image ? <img src={t.preview_image} alt="" className="w-24 h-20 object-cover shrink-0" /> : <div className="w-24 h-20 bg-navy border border-white/10 shrink-0" />}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-white font-medium truncate">{t.title}</span>
                  <span data-testid={`template-status-${t.id}`} className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 border rounded-full ${t.is_active ? "bg-green-500/15 text-green-300 border-green-500/30" : "bg-white/10 text-gray-400 border-white/20"}`}>{t.is_active ? "Live" : "Hidden"}</span>
                  {t.status && <span className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 border rounded-full ${STATUS_CLR[t.status] || STATUS_CLR.Draft}`}>{t.status}</span>}
                  {t.featured && <span className="text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 border rounded-full bg-gold/15 text-gold border-gold/40">Featured</span>}
                </div>
                <div className="text-gray-500 text-sm">{t.category}{t.industry ? ` · ${t.industry}` : ""} · {money(t.template_price)} / {money(t.custom_price)}</div>
                <ReadinessBar r={t.readiness} id={t.id} />
                <div className="flex gap-3 mt-3 flex-wrap text-sm">
                  <button onClick={() => openEdit(t)} data-testid={`edit-template-${t.id}`} className="text-gray-400 hover:text-gold flex items-center gap-1"><Pencil size={14} /> Edit</button>
                  <label data-testid={`upload-zip-inline-${t.id}`} className={`flex items-center gap-1 cursor-pointer ${busyId === t.id ? "text-gold" : "text-gray-400 hover:text-gold"}`} title="Upload/replace the downloadable .zip (auto-hosts the live demo)">
                    {busyId === t.id ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} {t.source_key ? "Update ZIP" : "Upload ZIP"}
                    <input type="file" accept=".zip,application/zip" className="hidden" disabled={busyId === t.id}
                      onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; uploadZipInline(t, f); }} />
                  </label>
                  <button onClick={() => toggleFeatured(t)} disabled={busyId === t.id} data-testid={`feature-template-${t.id}`} className={`flex items-center gap-1 ${t.featured ? "text-gold" : "text-gray-400 hover:text-gold"}`}><Star size={14} /> {t.featured ? "Unfeature" : "Feature"}</button>
                  <button onClick={() => toggleActive(t)} disabled={busyId === t.id} data-testid={`toggle-template-${t.id}`} className="text-gray-400 hover:text-white flex items-center gap-1">{t.is_active ? <><EyeOff size={14} /> Hide</> : <><Eye size={14} /> Publish</>}</button>
                  <button onClick={() => duplicate(t)} disabled={busyId === t.id} data-testid={`duplicate-template-${t.id}`} className="text-gray-400 hover:text-gold flex items-center gap-1"><Copy size={14} /> Duplicate</button>
                  {t.slug && <a href={`/marketplace/template/${t.slug}`} target="_blank" rel="noreferrer" data-testid={`preview-template-${t.id}`} className="text-gray-400 hover:text-gold flex items-center gap-1"><ExternalLink size={14} /> Preview</a>}
                  <button onClick={() => delPermanent(t)} disabled={busyId === t.id} data-testid={`archive-template-${t.id}`} className="text-gray-500 hover:text-red-400 flex items-center gap-1"><Archive size={14} /> Archive</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
      </>)}
      {editing && <TemplateEditor key={editing.id || "new"} tpl={editing} onClose={() => setEditing(null)} onSave={save} />}
    </div>
  );
}

function UploadBox({ label, hint, accept, onChange, busy, testid }) {
  return (
    <label className="block border border-dashed border-white/15 p-4 cursor-pointer hover:border-gold/40 transition-colors" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-sm text-white font-medium">{label}</span>
        {busy ? <Loader2 className="animate-spin text-gold" size={16} /> : <span className="text-gold text-xs">Choose file</span>}
      </div>
      <p className="text-gray-500 text-xs mt-1">{hint}</p>
      <input type="file" accept={accept} className="hidden" onChange={onChange} disabled={busy} />
    </label>
  );
}

function Section({ title, hint, open, onToggle, children, testid }) {
  return (
    <div className="border border-white/10 bg-navy/40" data-testid={testid}>
      <button type="button" onClick={onToggle} data-testid={`${testid}-toggle`} className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-white/5 transition-colors">
        <span className="text-white font-medium text-sm">{title}{hint && <span className="text-gray-500 text-xs font-normal ml-2">{hint}</span>}</span>
        <ChevronDown size={16} className={`text-gray-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-4 pb-4 pt-1 space-y-4">{children}</div>}
    </div>
  );
}

function CheckGrid({ options, selected, onToggle, testidPrefix }) {
  const sel = new Set(selected || []);
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid={testidPrefix}>
      {options.map((opt) => (
        <label key={opt} data-testid={`${testidPrefix}-${slugify(opt)}`}
          className={`flex items-center gap-2 text-xs px-2.5 py-2 border cursor-pointer transition-colors ${sel.has(opt) ? "border-gold/50 bg-gold/10 text-white" : "border-white/10 text-gray-400 hover:border-white/25"}`}>
          <input type="checkbox" className="accent-gold shrink-0" checked={sel.has(opt)} onChange={() => onToggle(opt)} />
          <span className="truncate">{opt}</span>
        </label>
      ))}
    </div>
  );
}

function TemplateEditor({ tpl, onClose, onSave }) {
  const [f, setF] = useState(tpl);
  const [busy, setBusy] = useState("");
  const [aiBusy, setAiBusy] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [relType, setRelType] = useState("minor");
  const [changelog, setChangelog] = useState("");
  const [verOverride, setVerOverride] = useState("");
  const [versions, setVersions] = useState([]);
  const [open, setOpen] = useState({ basics: true, classify: true, pricing: true });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggleSec = (k) => setOpen((p) => ({ ...p, [k]: !p[k] }));
  const toggleIn = (key, val) => setF((p) => {
    const cur = new Set(p[key] || []);
    cur.has(val) ? cur.delete(val) : cur.add(val);
    return { ...p, [key]: Array.from(cur) };
  });
  const setPage = (i, key, val) => setF((p) => {
    const arr = [...(p.pages_detail || [])];
    arr[i] = { ...arr[i], [key]: val };
    if (key === "name" && (!arr[i].slug || arr[i].slug === slugify(arr[i].name.slice(0, -1)))) arr[i].slug = slugify(val);
    return { ...p, pages_detail: arr };
  });
  const addPage = () => setF((p) => ({ ...p, pages_detail: [...(p.pages_detail || []), { name: "", slug: "", icon: "file", status: "Published" }] }));
  const removePage = (i) => setF((p) => ({ ...p, pages_detail: (p.pages_detail || []).filter((_, j) => j !== i) }));
  const ic = "w-full bg-navy border border-white/20 text-white p-2.5 text-sm focus:border-gold focus:outline-none";

  const loadVersions = useCallback(() => {
    if (!f.id) return;
    api.get(`/templates/${f.id}/versions`).then((r) => setVersions(r.data)).catch(() => {});
  }, [f.id]);
  useEffect(() => { loadVersions(); }, [loadVersions]);

  const genDesc = async () => {
    if (!f.title) { toast.error("Add a title first"); return; }
    setAiBusy(true);
    try {
      const { data } = await api.post("/templates/generate-description", {
        title: f.title, category: f.category, industry: f.industry || "",
        business_stage: f.business_stage || "", target_customer: f.target_customer || "",
        difficulty: f.difficulty || "", features: commaToArr(f.features), pages: commaToArr(f.pages),
        business_capabilities: f.business_capabilities || [], aipos_compatibility: f.aipos_compatibility || [],
        lifecycle_tags: f.sba_tags || [],
        template_price: Number(f.template_price) || 0, custom_price: Number(f.custom_price) || 0,
        badge: f.badge || "", seo_title: f.seo_title || "", focus_keywords: commaToArr(f.focus_keywords),
        internal_notes: f.internal_notes || "", tone: "professional",
      });
      setAiResult(data);
      setF((p) => ({
        ...p,
        description: data.description || p.description,
        meta_description: data.seo_meta_description || p.meta_description,
        seo_title: p.seo_title || data.sales_headline || "",
      }));
      toast.success(`Strategic copy drafted with AI (${data.provider})`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setAiBusy(false); }
  };

  const uploadZip = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy("zip");
    const fd = new FormData();
    fd.append("file", file); fd.append("release_type", relType); fd.append("changelog", changelog);
    if (verOverride.trim()) fd.append("version", verOverride.trim());
    try {
      const { data } = await api.post(`/templates/${f.id}/upload-zip`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Released v${data.version} · ${(data.source_size / 1048576).toFixed(1)}MB${data.live_demo ? " · live demo ready" : ""}`);
      if (versions.length > 0) toast.info("Customers on an older version will now see an update in their Download Center.");
      setF((p) => ({ ...p, source_size: data.source_size, live_demo: data.live_demo, current_version: data.version, current_version_id: data.version_id }));
      setChangelog(""); setVerOverride(""); loadVersions();
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(""); e.target.value = ""; }
  };
  const uploadImg = async (e, target) => {
    const file = e.target.files?.[0]; if (!file) return;
    setBusy(target);
    const fd = new FormData(); fd.append("file", file); fd.append("target", target);
    try {
      const { data } = await api.post(`/templates/${f.id}/upload-image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (target === "preview") setF((p) => ({ ...p, preview_image: data.url }));
      toast.success("Image uploaded");
    } catch (err) { toast.error(formatApiError(err)); } finally { setBusy(""); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-navy-800 border border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto p-8" onClick={(e) => e.stopPropagation()} data-testid="template-editor">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h3 className="font-heading text-2xl text-white">{tpl.id ? "Edit" : "New"} Template</h3>
            <p className="text-gray-500 text-xs mt-1">Register a production asset. Group fields below — advanced sections stay collapsed.</p>
          </div>
          <button onClick={onClose} aria-label="Close"><X className="text-gray-400 hover:text-white" /></button>
        </div>

        <div className="space-y-3">
          {/* BASICS */}
          <Section title="Basics" open={open.basics} onToggle={() => toggleSec("basics")} testid="section-basics">
            <div className="grid md:grid-cols-2 gap-4">
              <L label="Title"><input className={ic} value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="tpl-title" /></L>
              <L label="Slug (auto if blank)"><input className={ic} value={f.slug} onChange={(e) => set("slug", e.target.value)} data-testid="tpl-slug" placeholder={slugify(f.title)} /></L>
              <L label="Category"><input className={ic} value={f.category} onChange={(e) => set("category", e.target.value)} data-testid="tpl-category" /></L>
              <L label="Badge"><input className={ic} value={f.badge} onChange={(e) => set("badge", e.target.value)} /></L>
            </div>
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm text-gray-400">Description</label>
                <button type="button" onClick={genDesc} disabled={aiBusy} data-testid="tpl-generate-desc" className="flex items-center gap-1.5 text-xs text-gold hover:text-gold-light disabled:opacity-60">
                  {aiBusy ? <Loader2 className="animate-spin" size={13} /> : <Sparkles size={13} />} Generate with AI
                </button>
              </div>
              <textarea rows={4} className={ic} value={f.description} onChange={(e) => set("description", e.target.value)} data-testid="tpl-desc" />
            </div>
            {aiResult && (
              <div className="border border-gold/30 bg-gold/5 p-3 space-y-3 text-xs" data-testid="ai-suggestions">
                <div className="flex items-center gap-2 text-gold font-medium"><Sparkles size={13} /> AI strategy pack applied — review & tweak below</div>
                {aiResult.sales_headline && <div><div className="text-gray-500 uppercase tracking-wide text-[10px] mb-1">Sales headline</div><p className="text-gray-200" data-testid="ai-headline">{aiResult.sales_headline}</p></div>}
                {aiResult.summary && <div><div className="text-gray-500 uppercase tracking-wide text-[10px] mb-1">Short summary</div><p className="text-gray-200" data-testid="ai-summary">{aiResult.summary}</p></div>}
                {aiResult.key_benefits?.length > 0 && (
                  <div>
                    <div className="text-gray-500 uppercase tracking-wide text-[10px] mb-1">Key benefits</div>
                    <ul className="list-disc list-inside text-gray-200 space-y-0.5" data-testid="ai-benefits">{aiResult.key_benefits.map((b, i) => <li key={i}>{b}</li>)}</ul>
                    <button type="button" onClick={() => set("features", aiResult.key_benefits.join(", "))} data-testid="ai-use-benefits" className="mt-1 text-gold hover:text-gold-light">Use as Features →</button>
                  </div>
                )}
                {aiResult.ideal_customer && <div><div className="text-gray-500 uppercase tracking-wide text-[10px] mb-1">Ideal customer</div><p className="text-gray-200" data-testid="ai-ideal-customer">{aiResult.ideal_customer}</p></div>}
                {aiResult.seo_meta_description && <div><div className="text-gray-500 uppercase tracking-wide text-[10px] mb-1">SEO meta description (applied)</div><p className="text-gray-200" data-testid="ai-seo-meta">{aiResult.seo_meta_description}</p></div>}
              </div>
            )}
          </Section>

          {/* CLASSIFICATION */}
          <Section title="Classification" hint="Powers filtering, analytics & AI recommendations" open={open.classify} onToggle={() => toggleSec("classify")} testid="section-classify">
            <div className="grid md:grid-cols-2 gap-4">
              <L label="Industry"><select className={ic} value={f.industry || ""} onChange={(e) => set("industry", e.target.value)} data-testid="tpl-industry"><option value="">Select…</option>{INDUSTRIES.map((o) => <option key={o} value={o}>{o}</option>)}</select></L>
              <L label="Business Stage"><select className={ic} value={f.business_stage || ""} onChange={(e) => set("business_stage", e.target.value)} data-testid="tpl-stage"><option value="">Select…</option>{BUSINESS_STAGES.map((o) => <option key={o} value={o}>{o}</option>)}</select></L>
              <L label="Target Customer"><select className={ic} value={f.target_customer || ""} onChange={(e) => set("target_customer", e.target.value)} data-testid="tpl-target"><option value="">Select…</option>{TARGET_CUSTOMERS.map((o) => <option key={o} value={o}>{o}</option>)}</select></L>
              <L label="Difficulty"><select className={ic} value={f.difficulty || ""} onChange={(e) => set("difficulty", e.target.value)} data-testid="tpl-difficulty"><option value="">Select…</option>{DIFFICULTIES.map((o) => <option key={o} value={o}>{o}</option>)}</select></L>
            </div>
            <L label="SBA Lifecycle Tags"><CheckGrid options={SBA_TAGS} selected={f.sba_tags} onToggle={(v) => toggleIn("sba_tags", v)} testidPrefix="tpl-sba" /></L>
          </Section>

          {/* PRICING */}
          <Section title="Pricing" open={open.pricing} onToggle={() => toggleSec("pricing")} testid="section-pricing">
            <div className="grid md:grid-cols-3 gap-4">
              <L label="Regular Price ($)"><input type="number" className={ic} value={f.regular_price} onChange={(e) => set("regular_price", e.target.value)} data-testid="tpl-regular-price" /></L>
              <L label="Sale Price ($) — optional"><input type="number" className={ic} value={f.sale_price} onChange={(e) => set("sale_price", e.target.value)} data-testid="tpl-sale-price" placeholder="Leave blank for none" /></L>
              <L label="Custom Build Starting At ($)"><input type="number" className={ic} value={f.custom_price} onChange={(e) => set("custom_price", e.target.value)} data-testid="tpl-custom-price" /></L>
            </div>
            <div className="text-xs text-gray-400" data-testid="price-preview">
              Marketplace shows:{" "}
              {Number(f.sale_price) > 0
                ? <span><span className="line-through text-gray-500">${Number(f.regular_price || 0)}</span> <span className="text-gold font-semibold">${Number(f.sale_price)}</span></span>
                : <span className="text-white font-semibold">${Number(f.regular_price || 0)}</span>}
              {" · "}Custom build from <span className="text-white">${Number(f.custom_price || 0).toLocaleString()}</span>
            </div>
          </Section>

          {/* PAGES */}
          <Section title="Pages" hint={`${(f.pages_detail || []).length} pages`} open={!!open.pages} onToggle={() => toggleSec("pages")} testid="section-pages">
            <div className="space-y-2" data-testid="pages-builder">
              {(f.pages_detail || []).map((pg, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center" data-testid={`page-row-${i}`}>
                  <input className={ic + " col-span-4"} placeholder="Page name" value={pg.name} onChange={(e) => setPage(i, "name", e.target.value)} data-testid={`page-name-${i}`} />
                  <input className={ic + " col-span-3"} placeholder="slug" value={pg.slug} onChange={(e) => setPage(i, "slug", e.target.value)} data-testid={`page-slug-${i}`} />
                  <select className={ic + " col-span-2"} value={pg.icon} onChange={(e) => setPage(i, "icon", e.target.value)} data-testid={`page-icon-${i}`}>{PAGE_ICONS.map((o) => <option key={o} value={o}>{o}</option>)}</select>
                  <select className={ic + " col-span-2"} value={pg.status} onChange={(e) => setPage(i, "status", e.target.value)} data-testid={`page-status-${i}`}>{PAGE_STATUSES.map((o) => <option key={o} value={o}>{o}</option>)}</select>
                  <button type="button" onClick={() => removePage(i)} data-testid={`page-remove-${i}`} className="col-span-1 text-gray-500 hover:text-red-400 flex justify-center"><X size={15} /></button>
                </div>
              ))}
              {(f.pages_detail || []).length === 0 && <p className="text-xs text-gray-500">No pages yet — add the pages this template ships with.</p>}
            </div>
            <button type="button" onClick={addPage} data-testid="page-add" className="flex items-center gap-1.5 text-xs text-gold hover:text-gold-light mt-1"><Plus size={13} /> Add page</button>
          </Section>

          {/* FEATURES */}
          <Section title="Features" hint="Website functionality" open={!!open.features} onToggle={() => toggleSec("features")} testid="section-features">
            <L label="Features (comma separated)"><input className={ic} value={f.features} onChange={(e) => set("features", e.target.value)} data-testid="tpl-features" placeholder="Contact form, Image slider, Gallery, Animations, Live chat" /></L>
            <L label="Tech stack (comma separated)"><input className={ic} value={f.tech_stack} onChange={(e) => set("tech_stack", e.target.value)} data-testid="tpl-techstack" placeholder="Bootstrap 5, Vanilla JS" /></L>
          </Section>

          {/* BUSINESS CAPABILITIES */}
          <Section title="Business Capabilities" hint={`${(f.business_capabilities || []).length} selected`} open={!!open.caps} onToggle={() => toggleSec("caps")} testid="section-caps">
            <div className="space-y-4">
              {Object.entries(BUSINESS_CAPABILITY_GROUPS).map(([grp, opts]) => (
                <div key={grp}>
                  <div className="text-[11px] uppercase tracking-wider text-gold mb-2">{grp}</div>
                  <CheckGrid options={opts} selected={f.business_capabilities} onToggle={(v) => toggleIn("business_capabilities", v)} testidPrefix={`tpl-cap-${slugify(grp)}`} />
                </div>
              ))}
            </div>
          </Section>

          {/* AIPOS COMPATIBILITY */}
          <Section title="AIPOS Compatibility" hint="Compatibility flags — not activation switches" open={!!open.aipos} onToggle={() => toggleSec("aipos")} testid="section-aipos">
            <CheckGrid options={AIPOS_COMPATIBILITY} selected={f.aipos_compatibility} onToggle={(v) => toggleIn("aipos_compatibility", v)} testidPrefix="tpl-aipos" />
          </Section>

          {/* MEDIA */}
          <Section title="Media" open={!!open.media} onToggle={() => toggleSec("media")} testid="section-media">
            <L label="Preview image URL"><input className={ic} value={f.preview_image} onChange={(e) => set("preview_image", e.target.value)} data-testid="tpl-image" /></L>
            <L label="External demo URL (optional)"><input className={ic} value={f.demo_url} onChange={(e) => set("demo_url", e.target.value)} data-testid="tpl-demo-url" /></L>
            {f.preview_image && <img src={f.preview_image} alt="preview" className="h-24 object-cover border border-white/10" />}
          </Section>

          {/* PERFORMANCE */}
          <Section title="Performance Scores" open={!!open.perf} onToggle={() => toggleSec("perf")} testid="section-perf">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <L label="Performance"><input type="number" className={ic} value={f.metrics?.performance ?? 98} onChange={(e) => set("metrics", { ...f.metrics, performance: e.target.value })} data-testid="tpl-metric-performance" /></L>
              <L label="Accessibility"><input type="number" className={ic} value={f.metrics?.accessibility ?? 100} onChange={(e) => set("metrics", { ...f.metrics, accessibility: e.target.value })} data-testid="tpl-metric-accessibility" /></L>
              <L label="SEO"><input type="number" className={ic} value={f.metrics?.seo ?? 100} onChange={(e) => set("metrics", { ...f.metrics, seo: e.target.value })} data-testid="tpl-metric-seo" /></L>
              <L label="Load time (s)"><input type="number" step="0.1" className={ic} value={f.metrics?.load_time ?? 0.8} onChange={(e) => set("metrics", { ...f.metrics, load_time: e.target.value })} data-testid="tpl-metric-loadtime" /></L>
            </div>
          </Section>

          {/* SEO */}
          <Section title="SEO & Organization" open={!!open.seo} onToggle={() => toggleSec("seo")} testid="section-seo">
            <div className="grid md:grid-cols-2 gap-4">
              <L label="SEO Title"><input className={ic} value={f.seo_title || ""} onChange={(e) => set("seo_title", e.target.value)} data-testid="tpl-seo-title" /></L>
              <L label="Focus Keywords (comma separated)"><input className={ic} value={f.focus_keywords} onChange={(e) => set("focus_keywords", e.target.value)} data-testid="tpl-keywords" /></L>
            </div>
            <L label="Meta Description"><textarea rows={2} className={ic} value={f.meta_description || ""} onChange={(e) => set("meta_description", e.target.value)} data-testid="tpl-meta-desc" /></L>
          </Section>

          {/* MARKETPLACE & STATUS */}
          <Section title="Marketplace & Status" hint="Merchandising, discovery & release state" open={!!open.market} onToggle={() => toggleSec("market")} testid="section-market">
            <div className="grid md:grid-cols-2 gap-4">
              <L label="Series"><input className={ic} list="atf-series" value={f.series || ""} onChange={(e) => set("series", e.target.value)} data-testid="tpl-series" placeholder="e.g. Contractor Series" /><datalist id="atf-series">{SERIES.map((o) => <option key={o} value={o} />)}</datalist></L>
              <L label="Template Tags (SEO, search, AI, related)"><input className={ic} value={f.tags} onChange={(e) => set("tags", e.target.value)} data-testid="tpl-tags" placeholder="lawn care, landscaping, contractor" /></L>
              <L label="Production Status"><select className={ic} value={f.status || "Draft"} onChange={(e) => set("status", e.target.value)} data-testid="tpl-prod-status">{STATUSES.map((o) => <option key={o} value={o}>{o}</option>)}</select></L>
              <L label="Marketplace Visibility"><select className={ic} value={f.marketplace_visibility || "Public"} onChange={(e) => set("marketplace_visibility", e.target.value)} data-testid="tpl-visibility">{VISIBILITIES.map((o) => <option key={o} value={o}>{o}</option>)}</select></L>
            </div>
            <L label="Marketplace Badges"><CheckGrid options={BADGES} selected={f.badges} onToggle={(v) => toggleIn("badges", v)} testidPrefix="tpl-badge" /></L>
            <div className="grid md:grid-cols-2 gap-4 border-t border-white/10 pt-4">
              <L label="Hosting & Care Classification"><select className={ic} value={f.care_classification || ""} onChange={(e) => set("care_classification", e.target.value)} data-testid="tpl-care-classification"><option value="">Select…</option>{CARE_CLASSIFICATIONS.map((o) => <option key={o} value={o}>{o}</option>)}</select></L>
              <L label="Last Reviewed">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-300 flex-1" data-testid="tpl-last-reviewed">{f.last_qa_review ? new Date(f.last_qa_review).toLocaleDateString() : "Never reviewed"}</span>
                  <button type="button" onClick={() => set("last_qa_review", new Date().toISOString())} data-testid="tpl-mark-reviewed" className="text-xs text-gold border border-gold/40 px-2.5 py-1 hover:bg-gold/10 shrink-0">Mark reviewed today</button>
                </div>
              </L>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-300 cursor-pointer">
              <input type="checkbox" className="accent-gold" checked={!!f.readiness_exception} onChange={(e) => set("readiness_exception", e.target.checked)} data-testid="tpl-readiness-exception" />
              Approved exception — allow publishing this design below 100% readiness
            </label>
            <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500 border-t border-white/10 pt-3" data-testid="market-meta">
              <span>Version: <span className="text-gray-300">v{f.current_version || "—"}</span></span>
              <span>Last Updated: <span className="text-gray-300">{f.updated_at ? new Date(f.updated_at).toLocaleDateString() : "—"}</span></span>
              {f.series && <span>Series: <span className="text-gray-300">{f.series}</span></span>}
            </div>
          </Section>

          {/* SOLUTION FACTORY — edit mode only (needs a saved product id) */}
          {f.id && (
            <Section title="Solution Profile" hint="AI draft → review → publish" open={!!open.solution} onToggle={() => toggleSec("solution")} testid="section-solution">
              <SolutionFactory templateId={f.id} title={f.title} />
            </Section>
          )}

          {/* PRODUCTION NOTES (admin only) */}
          <Section title="Production Notes" hint="Private — never shown to customers" open={!!open.notes} onToggle={() => toggleSec("notes")} testid="section-notes">
            {(f.created_by || f.current_version) && (
              <div className="flex flex-wrap gap-4 text-xs text-gray-500">
                {f.created_by && <span>Created by <span className="text-gray-300">{f.created_by}</span></span>}
                {f.current_version && <span>Version <span className="text-gray-300">v{f.current_version}</span></span>}
                {f.updated_at && <span>Updated <span className="text-gray-300">{new Date(f.updated_at).toLocaleDateString()}</span></span>}
              </div>
            )}
            <L label="Internal Notes"><textarea rows={2} className={ic} value={f.internal_notes || ""} onChange={(e) => set("internal_notes", e.target.value)} data-testid="tpl-internal-notes" placeholder="Private production notes…" /></L>
            <L label="AI Notes"><textarea rows={2} className={ic} value={f.ai_notes || ""} onChange={(e) => set("ai_notes", e.target.value)} data-testid="tpl-ai-notes" placeholder="Reserved for AIPOS recommendations & optimization suggestions." /></L>
          </Section>
        </div>

        {/* FILES / VERSIONS / DEMO — edit mode only */}
        {f.id ? (
          <div className="mt-6 border-t border-white/10 pt-6 space-y-5" data-testid="template-uploads">
            <div className="flex items-center justify-between">
              <h4 className="font-heading text-lg text-white">Files, Versions & Live Demo</h4>
              {f.current_version && <span className="text-xs text-gold border border-gold/40 px-2.5 py-1" data-testid="editor-current-version">Current: v{f.current_version}</span>}
            </div>
            <div className="bg-navy/50 border border-white/10 p-4 space-y-3" data-testid="release-version">
              <div className="text-sm text-white font-medium">Release a new version</div>
              <div className="grid grid-cols-2 gap-3">
                <L label="Release type">
                  <select className={ic} value={relType} onChange={(e) => setRelType(e.target.value)} data-testid="release-type">
                    <option value="major">Major (breaking / redesign)</option>
                    <option value="minor">Minor (features)</option>
                    <option value="security">Security / patch</option>
                  </select>
                </L>
                <L label="Version override (optional)"><input className={ic} value={verOverride} onChange={(e) => setVerOverride(e.target.value)} placeholder="auto" data-testid="version-override" /></L>
              </div>
              <L label="Changelog / release notes"><textarea rows={2} className={ic} value={changelog} onChange={(e) => setChangelog(e.target.value)} placeholder="What changed in this release?" data-testid="release-changelog" /></L>
              <UploadBox label={versions.length ? "Upload new .zip → release version" : "Upload .zip → v1.0.0"} testid="upload-zip" busy={busy === "zip"} accept=".zip,application/zip" onChange={uploadZip}
                hint={f.source_size ? `Latest · ${(f.source_size / 1048576).toFixed(1)}MB${f.live_demo ? " · live demo ✓" : ""}. Uploading creates a new version buyers can download.` : "Buyers download this. If it has index.html, a live demo is auto-hosted."} />
            </div>
            {versions.length > 0 && (
              <div className="space-y-2" data-testid="editor-version-history">
                <div className="text-sm text-gray-400">Version history ({versions.length})</div>
                {versions.map((v) => (
                  <div key={v.id} className="flex items-start gap-3 bg-navy/40 border border-white/5 p-3">
                    <span className="text-white text-sm font-medium shrink-0">v{v.version}</span>
                    <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 shrink-0 ${v.release_type === "major" ? "bg-gold/20 text-gold" : v.release_type === "security" ? "bg-red-500/15 text-red-300" : "bg-blue-500/15 text-blue-300"}`}>{v.release_type}</span>
                    <span className="text-gray-400 text-sm flex-1">{v.changelog}</span>
                    <span className="text-gray-600 text-xs shrink-0">{new Date(v.created_at).toLocaleDateString()}</span>
                  </div>
                ))}
              </div>
            )}
            <div className="grid md:grid-cols-2 gap-4">
              <UploadBox label="Preview image" testid="upload-preview" busy={busy === "preview"} accept="image/*" onChange={(e) => uploadImg(e, "preview")} hint="Main marketplace image (upload from your computer)." />
              <UploadBox label="Add gallery image" testid="upload-gallery" busy={busy === "gallery"} accept="image/*" onChange={(e) => uploadImg(e, "gallery")} hint="Extra screenshots shown on the detail page." />
            </div>
          </div>
        ) : (
          <div className="mt-6 border-t border-white/10 pt-6 text-sm text-gray-400" data-testid="upload-hint">Fill in the details above and click <span className="text-gold">Save Template</span> — the .zip &amp; image upload section will appear here automatically.</div>
        )}

        <div className="flex justify-end gap-3 mt-8">
          <button onClick={onClose} className="px-6 py-2.5 border border-white/20 text-white hover:bg-white/5">Close</button>
          <button onClick={() => onSave(f)} data-testid="save-template-btn" className="px-6 py-2.5 bg-gold text-navy font-semibold hover:bg-gold-light">Save Template</button>
        </div>
      </div>
    </div>
  );
}
